import { useState } from "react";
import { toast } from "sonner";
import { Upload, Github, Loader2, CalendarDays, MessageSquareText } from "lucide-react";
import { learn } from "@/lib/learnApi";
import { errorText } from "@/lib/api";

const TONE = { Submitted: "bg-sky-50 text-sky-800", Reviewed: "bg-[#0E9384] text-white", "Needs changes": "bg-[#0B1B33] text-white" };

const SubmitForm = ({ a, onDone }) => {
  const [file, setFile] = useState(null);
  const [link, setLink] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file && !link) return toast.error("Add a file or a GitHub link.");
    if (file && file.size > 25 * 1048576) return toast.error("Max file size is 25 MB.");
    const fd = new FormData();
    fd.append("assignment_id", a.id); fd.append("github_url", link); fd.append("note", note);
    if (file) fd.append("file", file);
    setBusy(true);
    try { const sub = await learn.submit(fd); toast.success("Submitted. You'll get an email when it's reviewed."); onDone(sub); setFile(null); setLink(""); setNote(""); }
    catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  return (
    <form data-testid={`submit-form-${a.id}`} onSubmit={submit} className="mt-4 grid gap-3 rounded-xl bg-slate-50 p-4 md:grid-cols-2">
      <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-3 text-[14px] text-slate-700 hover:border-[#0E9384]">
        <Upload className="h-4 w-4 text-[#0E9384]" /><span className="truncate">{file ? file.name : "Upload file (PDF, ZIP, PPT · max 25 MB)"}</span>
        <input data-testid={`submit-file-${a.id}`} type="file" accept=".pdf,.zip,.ppt,.pptx,.doc,.docx,.ipynb,.py,.md,.txt" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </label>
      <label className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3"><Github className="h-4 w-4 text-slate-600" />
        <input data-testid={`submit-link-${a.id}`} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://github.com/you/project" className="h-11 flex-1 bg-transparent text-[14px] outline-none" /></label>
      <input data-testid={`submit-note-${a.id}`} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="Short note: what you built, what's still missing" className="h-11 rounded-lg border border-slate-300 bg-white px-3 text-[14px] outline-none md:col-span-2" />
      <button data-testid={`submit-btn-${a.id}`} disabled={busy} className="btn-teal flex items-center justify-center gap-2 rounded-lg py-3 font-semibold disabled:opacity-60 md:col-span-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit project"}</button>
    </form>
  );
};

export const Assignments = ({ items, subs, onSubmitted }) => {
  if (!items.length) return <p data-testid="no-assignments" className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-600">No projects assigned yet. They'll appear here as soon as they're posted.</p>;
  return (
    <div className="space-y-4">
      {items.map((a) => {
        const mine = subs.filter((s) => s.assignment_id === a.id).sort((x, y) => y.submitted_at.localeCompare(x.submitted_at));
        return (
          <article key={a.id} data-testid={`assignment-${a.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h3 className="font-display text-[19px] font-bold text-[#0B1B33]">{a.title}</h3>
              {a.due && <span className="flex items-center gap-1.5 rounded-full bg-sky-50 px-3 py-1 text-[13px] font-semibold text-sky-800"><CalendarDays className="h-3.5 w-3.5" />Due {new Date(a.due).toLocaleDateString()}</span>}
            </div>
            {a.instructions && <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-slate-700">{a.instructions}</p>}
            {mine.map((s) => (
              <div key={s.id} className="mt-3 rounded-xl border border-slate-200 p-3 text-[14px]">
                <div className="flex flex-wrap items-center gap-2"><span className={`rounded-full px-2.5 py-0.5 text-[12px] font-semibold ${TONE[s.status]}`}>{s.status}</span><span className="text-slate-500">{new Date(s.submitted_at).toLocaleString()}</span>
                  {s.filename && <span className="text-slate-700">· {s.filename}</span>}{s.github_url && <a href={s.github_url} target="_blank" rel="noreferrer" className="text-[#0E9384] underline">link</a>}</div>
                {s.feedback && <p className="mt-2 flex gap-2 text-slate-800"><MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-[#0E9384]" />{s.feedback}</p>}
              </div>
            ))}
            <SubmitForm a={a} onDone={onSubmitted} />
          </article>
        );
      })}
    </div>
  );
};
